import { useState, useEffect } from "react";
import { getData } from "../utils/localCrud";
import "./Dashboard.css";

export default function Dashboard() {
    const [stats, setStats] = useState({
        users: 0,
        members: 0,
        donations: 0,
        events: 0,
        activeUsers: 0,
        adminUsers: 0,
        todayUsers: 0,
        totalAmount: 0
    });

    const [recentUsers, setRecentUsers] = useState([]);
    const [recentActivities, setRecentActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [userGrowth, setUserGrowth] = useState([]);
    const [roleDistribution, setRoleDistribution] = useState({});

    const API_URL = process.env.REACT_APP_API_URL || "https://jspstrust.com/api/api/";

    useEffect(() => {
        loadDashboardData();
        // Auto-refresh every 30 seconds
        const interval = setInterval(loadDashboardData, 30000);
        return () => clearInterval(interval);
    }, []);

    const loadDashboardData = async () => {
        setLoading(true);

        try {
            // Fetch dashboard data from your new API
            const res = await fetch(`${API_URL}Dashboard.php`);
            const data = await res.json();

            if (data.status === "success") {
                // Set stats from API
                setStats({
                    users: data.stats.users || 0,
                    members: data.stats.members || 0,
                    donations: data.stats.donations || 0,
                    events: data.stats.events || 0,
                    activeUsers: data.stats.activeUsers || 0,
                    adminUsers: data.stats.adminUsers || 0,
                    todayUsers: data.stats.todayUsers || 0,
                    totalAmount: data.stats.totalAmount || 0
                });

                // Set recent users from API
                setRecentUsers(data.recentUsers || []);

                // Set user growth from API
                setUserGrowth(data.userGrowth || []);

                // Set role distribution from API
                setRoleDistribution(data.roleDistribution || {});

                // Generate activities based on API data
                const activities = generateRecentActivities(
                    data.recentUsers || [],
                    data.stats
                );
                setRecentActivities(activities);
            } else {
                console.error("API returned error:", data.message);
                // Set empty/default values on error
                setStats({
                    users: 0,
                    members: 0,
                    donations: 0,
                    events: 0,
                    activeUsers: 0,
                    adminUsers: 0,
                    todayUsers: 0,
                    totalAmount: 0
                });
            }
        } catch (error) {
            console.error("Failed to load dashboard data:", error);
            // Fallback to showing zeros on error
            setStats({
                users: 0,
                members: 0,
                donations: 0,
                events: 0,
                activeUsers: 0,
                adminUsers: 0,
                todayUsers: 0,
                totalAmount: 0
            });
        } finally {
            setLoading(false);
        }
    };

    const generateRecentActivities = (users, stats) => {
        const activities = [];

        // Add user activities
        if (users && users.length > 0) {
            users.slice(0, 3).forEach(user => {
                activities.push({
                    id: `user-${user.id}`,
                    type: "user",
                    action: "created",
                    title: `New User: ${user.name}`,
                    description: `${user.role} account created`,
                    time: getTimeAgo(user.created_at || user.createdAt),
                    icon: "👤",
                    color: "#4CAF50"
                });
            });
        }

        // Add stats-based activities
        if (stats.todayUsers > 0) {
            activities.push({
                id: "today-users",
                type: "growth",
                action: "increased",
                title: `${stats.todayUsers} New Users Today`,
                description: "User growth continues",
                time: "Today",
                icon: "📈",
                color: "#2196F3"
            });
        }

        if (stats.events > 0) {
            activities.push({
                id: "events-count",
                type: "event",
                action: "upcoming",
                title: `${stats.events} Events Scheduled`,
                description: "Check upcoming events",
                time: "Active",
                icon: "📅",
                color: "#9C27B0"
            });
        }

        if (stats.members > 0) {
            activities.push({
                id: "members-count",
                type: "member",
                action: "active",
                title: `${stats.members} Active Members`,
                description: "Organization members",
                time: "Current",
                icon: "⭐",
                color: "#FF9800"
            });
        }

        // If no activities, add sample ones
        if (activities.length === 0) {
            return [
                {
                    id: "1",
                    type: "system",
                    action: "welcome",
                    title: "Welcome to Dashboard!",
                    description: "Start by creating your first user",
                    time: "Just now",
                    icon: "🚀",
                    color: "#607D8B"
                },
                {
                    id: "2",
                    type: "tip",
                    action: "tip",
                    title: "Quick Tip",
                    description: "Use the 'Create User' button to add new users",
                    time: "2 mins ago",
                    icon: "💡",
                    color: "#FFC107"
                }
            ];
        }

        return activities.slice(0, 5);
    };

    const getTimeAgo = (dateString) => {
        if (!dateString) return "Recently";

        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return "Just now";
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return new Date(dateString).toLocaleDateString();
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const handleQuickAction = (action) => {
        switch (action) {
            case 'createUser':
                alert("Redirecting to create user...");
                // window.location.href = "/users/create";
                break;
            case 'exportData':
                alert("Exporting data...");
                break;
            case 'sendEmail':
                alert("Opening email composer...");
                break;
            case 'refresh':
                loadDashboardData();
                break;
            default:
                break;
        }
    };

    const handleLogout = () => {
        // Clear all user data
        localStorage.clear();
        sessionStorage.clear();
        
        // Clear cookies
        document.cookie = "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        document.cookie = "userData=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        
        console.log("User logged out successfully");
        
        // Redirect to login page
        window.location.href = '/login';
    };

    return (
        <div className="dashboard">
            {/* Header Section */}
            <div className="dashboard-header">
                <h2><span>📊</span> Dashboard Overview</h2>
                <div className="header-actions">
                    <button
                        className="refresh-btn"
                        onClick={() => loadDashboardData()}
                        disabled={loading}
                    >
                        {loading ? "Refreshing..." : "🔄 Refresh"}
                    </button>
                    
                    <button
                        className="logout-btn"
                        onClick={handleLogout}
                        style={{
                            marginLeft: '10px',
                            padding: '8px 16px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: '500'
                        }}
                    >
                        🚪 Logout
                    </button>
                    
                    <div className="current-time">
                        {new Date().toLocaleDateString('en-IN', {
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                        })}
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>Loading dashboard data...</p>
                </div>
            ) : (
                <>
                    {/* Stats Cards Grid */}
                    <div className="stats-grid">
                        <div className="stat-card primary">
                            <div className="stat-icon">👥</div>
                            <div className="stat-content">
                                <h3>Total Users</h3>
                                <p className="stat-value">{stats.users}</p>
                                <p className="stat-subtext">
                                    <span className="trend up">↑ {stats.todayUsers} today</span>
                                    • {stats.activeUsers} active
                                </p>
                            </div>
                        </div>

                        <div className="stat-card success">
                            <div className="stat-icon">⭐</div>
                            <div className="stat-content">
                                <h3>Members</h3>
                                <p className="stat-value">{stats.members}</p>
                                <p className="stat-subtext">Organization members</p>
                            </div>
                        </div>

                        <div className="stat-card info">
                            <div className="stat-icon">💰</div>
                            <div className="stat-content">
                                <h3>Donations</h3>
                                <p className="stat-value">{stats.donations}</p>
                                <p className="stat-subtext">
                                    Total: {formatCurrency(stats.totalAmount)}
                                </p>
                            </div>
                        </div>

                        <div className="stat-card warning">
                            <div className="stat-icon">📅</div>
                            <div className="stat-content">
                                <h3>Events</h3>
                                <p className="stat-value">{stats.events}</p>
                                <p className="stat-subtext">Upcoming events</p>
                            </div>
                        </div>

                        <div className="stat-card secondary">
                            <div className="stat-icon">👑</div>
                            <div className="stat-content">
                                <h3>Admin Users</h3>
                                <p className="stat-value">{stats.adminUsers}</p>
                                <p className="stat-subtext">
                                    {stats.users > 0 && stats.adminUsers > 0
                                        ? `${Math.round((stats.adminUsers / stats.users) * 100)}% of total`
                                        : "No admins"
                                    }
                                </p>
                            </div>
                        </div>

                        <div className="stat-card dark">
                            <div className="stat-icon">📈</div>
                            <div className="stat-content">
                                <h3>User Growth</h3>
                                <p className="stat-value">
                                    {stats.users > 0 ? "+" : ""}{stats.users}
                                </p>
                                <p className="stat-subtext">
                                    Last 7 days: {userGrowth.reduce((sum, day) => sum + day.users, 0)} new
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Main Content Grid */}
                    <div className="dashboard-grid">
                        {/* Left Column - Charts & Recent Users */}
                        <div className="left-column">
                            {/* User Growth Chart */}
                            <div className="chart-card">
                                <div className="chart-header">
                                    <h3>📈 User Growth (Last 7 Days)</h3>
                                    <span className="chart-badge">Live</span>
                                </div>
                                <div className="chart-container">
                                    {userGrowth.length > 0 ? (
                                        <div className="bar-chart">
                                            {userGrowth.map((day, index) => {
                                                const maxUsers = Math.max(...userGrowth.map(d => d.users), 1);
                                                return (
                                                    <div key={index} className="bar-item">
                                                        <div className="bar-label">{day.date}</div>
                                                        <div className="bar-wrapper">
                                                            <div
                                                                className="bar-fill"
                                                                style={{
                                                                    height: `${Math.max(10, (day.users / maxUsers) * 80)}%`
                                                                }}
                                                                title={`${day.users} users on ${day.fullDate}`}
                                                            ></div>
                                                        </div>
                                                        <div className="bar-value">{day.users}</div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="no-chart-data">
                                            <p>No user data available for chart</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Recent Users Table */}
                            <div className="recent-users-card">
                                <div className="card-header">
                                    <h3>👥 Recent Users</h3>
                                    <button
                                        className="view-all-btn"
                                        onClick={() => handleQuickAction('createUser')}
                                    >
                                        + Add New
                                    </button>
                                </div>
                                <div className="users-table">
                                    {recentUsers.length > 0 ? (
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>Name</th>
                                                    <th>Email</th>
                                                    <th>Role</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {recentUsers.map(user => (
                                                    <tr key={user.id}>
                                                        <td>
                                                            <div className="user-cell">
                                                                <div className="user-avatar">
                                                                    {user.name?.charAt(0).toUpperCase()}
                                                                </div>
                                                                <span>{user.name}</span>
                                                            </div>
                                                        </td>
                                                        <td>{user.email}</td>
                                                        <td>
                                                            <span className={`role-badge ${(user.role || '').toLowerCase()}`}>
                                                                {user.role}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className={`status-badge ${(user.status || '').toLowerCase()}`}>
                                                                {user.status}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <div className="empty-state">
                                            <p>No users found. Create your first user!</p>
                                            <button
                                                className="create-btn"
                                                onClick={() => handleQuickAction('createUser')}
                                            >
                                                Create User
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Right Column - Activity & Quick Actions */}
                        <div className="right-column">
                            {/* Recent Activity */}
                            <div className="activity-card">
                                <div className="card-header">
                                    <h3>🔔 Recent Activity</h3>
                                    <span className="activity-count">{recentActivities.length}</span>
                                </div>
                                <div className="activity-list">
                                    {recentActivities.map(activity => (
                                        <div key={activity.id} className="activity-item">
                                            <div
                                                className="activity-icon"
                                                style={{ backgroundColor: activity.color }}
                                            >
                                                {activity.icon}
                                            </div>
                                            <div className="activity-content">
                                                <div className="activity-header">
                                                    <h4>{activity.title}</h4>
                                                    <span className="activity-time">{activity.time}</span>
                                                </div>
                                                <p className="activity-desc">{activity.description}</p>
                                                <div className="activity-meta">
                                                    <span className="activity-type">{activity.type}</span>
                                                    <span className="activity-action">{activity.action}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {recentActivities.length === 0 && (
                                    <div className="activity-empty">
                                        <p>No activities yet</p>
                                        <small>Activities will appear here as you use the system</small>
                                    </div>
                                )}
                            </div>

                            {/* Quick Actions */}
                            <div className="quick-actions-card">
                                <div className="card-header">
                                    <h3>⚡ Quick Actions</h3>
                                </div>
                                <div className="actions-grid">
                                    <button
                                        className="action-btn primary"
                                        onClick={() => handleQuickAction('createUser')}
                                    >
                                        <span className="action-icon">👤</span>
                                        <span className="action-text">Create User</span>
                                    </button>
                                    <button
                                        className="action-btn success"
                                        onClick={() => handleQuickAction('exportData')}
                                    >
                                        <span className="action-icon">📤</span>
                                        <span className="action-text">Export Data</span>
                                    </button>
                                    <button
                                        className="action-btn info"
                                        onClick={() => handleQuickAction('sendEmail')}
                                    >
                                        <span className="action-icon">✉️</span>
                                        <span className="action-text">Send Email</span>
                                    </button>
                                    <button
                                        className="action-btn warning"
                                        onClick={() => handleQuickAction('refresh')}
                                    >
                                        <span className="action-icon">🔄</span>
                                        <span className="action-text">Refresh Data</span>
                                    </button>
                                </div>
                            </div>

                            {/* Role Distribution */}
                            <div className="roles-card">
                                <div className="card-header">
                                    <h3>🎯 User Roles</h3>
                                </div>
                                <div className="roles-list">
                                    {Object.keys(roleDistribution).length > 0 ? (
                                        <>
                                            {Object.entries(roleDistribution).map(([role, count]) => {
                                                const percentage = stats.users > 0
                                                    ? Math.round((count / stats.users) * 100)
                                                    : 0;
                                                return (
                                                    <div key={role} className="role-item">
                                                        <div className="role-info">
                                                            <span className="role-name">{role}</span>
                                                            <span className="role-count">{count} users</span>
                                                        </div>
                                                        <div className="role-bar">
                                                            <div
                                                                className="role-fill"
                                                                style={{ width: `${percentage}%` }}
                                                            ></div>
                                                        </div>
                                                        <span className="role-percentage">{percentage}%</span>
                                                    </div>
                                                );
                                            })}
                                        </>
                                    ) : (
                                        <div className="no-roles">
                                            <p>No role data available</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer Stats */}
                    <div className="footer-stats">
                        <div className="footer-stat">
                            <span className="footer-label">System Status</span>
                            <span className="footer-value online">● Online</span>
                        </div>
                        <div className="footer-stat">
                            <span className="footer-label">Last Updated</span>
                            <span className="footer-value">
                                {new Date().toLocaleTimeString('en-IN')}
                            </span>
                        </div>
                        <div className="footer-stat">
                            <span className="footer-label">Data Version</span>
                            <span className="footer-value">v1.0.0</span>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}